import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import GrievanceStepCard from '../components/GrievanceStepCard';
import GrievanceStepModal from '../components/GrievanceStepModal';
import ConfirmDialog from '../components/ConfirmDialog';

const GrievancesRedressalStepsPage = () => {
    const [steps, setSteps] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Three-Step Resolution Process',
        section_subtitle: "Clear pathways to resolve your concerns"
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStep, setEditingStep] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, stepId: null });

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            console.log('Fetching grievance process data...');
            const [stepsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/grievance-process/steps'),
                axios.get('/api/admin/grievance-process/settings')
            ]);
            console.log('Steps Response:', stepsRes.data);
            console.log('Settings Response:', settingsRes.data);

            setSteps(stepsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            if (error.response) {
                console.error('Data:', error.response.data);
                console.error('Status:', error.response.status);
            }
            toast.error('Failed to load steps data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingStep) {
                await axios.put(`/api/admin/grievance-process/steps/${editingStep.id}`, formData);
                toast.success('Step updated successfully');
            } else {
                await axios.post('/api/admin/grievance-process/steps', formData);
                toast.success('Step created successfully');
            }
            setIsModalOpen(false);
            setEditingStep(null);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, stepId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/grievance-process/steps/${deleteConfirm.stepId}`);
            toast.success('Step deleted successfully');
            setDeleteConfirm({ isOpen: false, stepId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/grievance-process/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Resolution Process</h1>
                    <p className="text-slate-500 mt-1">Manage the steps for Grievance Redressal process</p>
                </div>
                <button
                    onClick={() => {
                        setEditingStep(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Step
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Three-Step Resolution Process"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Clear pathways to resolve your concerns"
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Steps Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-48 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : steps.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckBadgeIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No steps added yet</h3>
                        <p className="text-slate-500 mb-6">Create resolution steps to showcase on the page.</p>
                        <button
                            onClick={() => {
                                setEditingStep(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Step
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {steps.map((step) => (
                        <GrievanceStepCard
                            key={step.id}
                            step={step}
                            onEdit={(s) => {
                                setEditingStep(s);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <GrievanceStepModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingStep(null);
                }}
                onSubmit={handleSubmit}
                step={editingStep}
                loading={submitting}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, stepId: null })}
                onConfirm={confirmDelete}
                title="Delete Step"
                message="Are you sure you want to delete this step? This action cannot be undone."
            />
        </div>
    );
};

export default GrievancesRedressalStepsPage;
